import React, { useState } from 'react';
import { Suspense } from '../../components/Suspense';
import { ICalculateStatsStatus, SimpleTable, TableMobileType } from '../../components/Table/Table';
import { useTable } from '../../hooks/useTable';
import { IFieldConfig } from '../../models/Table';
import { TableFilters } from '../../components/TableFilters/TableFilters';
import { IFilters, useFilters } from '../TableFilters/useFilters';
import { transformFilters } from '../../utils/generateTableParams';
import styled from 'styled-components';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import IconButton from '@material-ui/core/IconButton';
import { AxiosArrayResponse } from '../../models/Response';
import { TableStatsChange } from '../TableFilters/TableStatsChange';
import { useStatChangeContext } from '../../hooks/useStatChangeContext';
import { StatsType } from '../../config/stats';

const StyledScrollContainer = styled.div<{ isFiltersOpen: boolean; isStatChangeVisible: boolean }>`
  height: ${({ isStatChangeVisible }) => (isStatChangeVisible ? 'calc(100vh - 13rem)' : 'calc(100vh - 7rem)')};
  overflow: auto;
  padding-bottom: ${({ isFiltersOpen }) => (isFiltersOpen ? '8.7rem' : '2rem')};
  margin: 0 -1rem;
`;

const Flex = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 1rem;
  padding: 0 1rem;

  span {
    margin-bottom: 10px;
    border-radius: 25px;
  }
`;

const FallbackTable = () => {
  return (
    <>
      <Hidden lgUp>
        <Flex>
          <Skeleton variant="rect" height={135} width="100%" />
          <Skeleton variant="rect" height={135} width="100%" />
          <Skeleton variant="rect" height={135} width="100%" />
          <Skeleton variant="rect" height={135} width="100%" />
          <Skeleton variant="rect" height={135} width="100%" />
        </Flex>
      </Hidden>
      <Hidden mdDown>
        <Flex>
          <Typography variant="h6" gutterBottom>
            Ładowanie danych...
          </Typography>
          <CircularProgress size={60} thickness={5} />
        </Flex>
      </Hidden>
    </>
  );
};
interface TableItemModalProps<ResponseType> {
  open: boolean;
  handleClose: () => void;
  match: ResponseType;
  date: IFilters['date'];
  statName?: StatsType;
}

type TableWithFiltersProps<ResponseType, Params> = {
  headerConfig: IFieldConfig[];
  apiMethod: (apiMethodParams: Params) => AxiosArrayResponse<ResponseType>;
  apiMethodParams?: any;
  transformCB?: (data: ResponseType[]) => any[];
  tableType?: TableMobileType;
  calculateStatsStatus?: (match: ResponseType) => ICalculateStatsStatus;
  renderTableModalItem?: (params: TableItemModalProps<ResponseType>) => JSX.Element;
  isPredictionFilters?: boolean;
};

export const TableWithFilters = <ResponseType, Params>({
  apiMethod,
  headerConfig,
  transformCB,
  apiMethodParams = {},
  tableType,
  calculateStatsStatus,
  renderTableModalItem,
  isPredictionFilters,
}: TableWithFiltersProps<ResponseType, Params>) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<ResponseType | null>(null);
  const { filters, setFilters } = useFilters(isPredictionFilters);

  const { isUnderOver, allStatsVariants } = useStatChangeContext();

  const tableProperties = useTable(
    {
      apiMethod: apiMethod,
      fieldsConfig: headerConfig,
      extraParams: {
        ...transformFilters(filters, isPredictionFilters),
        ...apiMethodParams,
      },
      transformResponse: (data) => {
        if (transformCB) return transformCB(data);
        return data;
      },
    },
    [filters.date, filters.sortType, filters.search],
  );

  const handleCloseModal = () => {
    setSelectedMatch(null);
  };
  const isStatChangeVisible = isUnderOver && allStatsVariants.length > 0;

  return (
    <div>
      <TableFilters setFilters={setFilters} filters={filters} handleFiltersOpen={setIsFiltersOpen} />
      <TableStatsChange />
      <StyledScrollContainer isFiltersOpen={isFiltersOpen} isStatChangeVisible={isStatChangeVisible}>
        <Suspense fallback={<FallbackTable />} requestStatus={tableProperties.requestStatus} tableData={tableProperties.response}>
          <SimpleTable
            data={tableProperties}
            keyId="id"
            roundedTop={false}
            mobileView={tableType}
            onClick={(match) => {
              setSelectedMatch(match);
            }}
            calculateStatsStatus={calculateStatsStatus}
            rowAction={(match) => {
              return (
                <IconButton
                  type="button"
                  size="small"
                  onClick={() => {
                    setSelectedMatch(match);
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              );
            }}
          />
        </Suspense>
      </StyledScrollContainer>
      {renderTableModalItem && selectedMatch
        ? renderTableModalItem({
            open: Boolean(selectedMatch),
            match: selectedMatch,
            handleClose: handleCloseModal,
            date: filters.date,
            statName: apiMethodParams.statName,
          })
        : null}
    </div>
  );
};
